import FlexContainer from '@/components/atomic/atoms/layout/flex-container/styled';
import Link from '@/components/atomic/atoms/navigation/link/styled';
import type { MenuItemProps } from '@/components/atomic/atoms/navigation/menu/item/typings';
import Navbar from '@/components/atomic/atoms/surfaces/navbar/styled';
import { BasicComponentProps } from '@/typings/generic';
import styled, { CSSObject } from '@emotion/styled';

const MenuItem = styled(FlexContainer)<MenuItemProps & BasicComponentProps>(
	({
		theme,
		$isActive,
		$hasSeparator,
		$hasLabel,
		$LinkProps,
		$breakpoints,
	}) => {
		const style: CSSObject = {
			display: 'block',
			float: 'left',
			[theme.media.desktop.laptop]: {
				display: 'inline-block',
				height: 72,
				...($hasSeparator
					? {
							position: 'relative',
						}
					: {}),
				[':before']: {
					...($hasSeparator
						? {
								content: '""',
								position: 'absolute',
								left: -0.5,
								top: 15,
								bottom: 15,
								width: 1,
								backgroundColor: theme.colors.keylineColour,
							}
						: {}),
				},
			},
		};

		let linkStylePaddingValue = 16;
		let linkStylePaddingValueLaptop = 10;
		if ($hasSeparator && !$hasLabel) {
			linkStylePaddingValue = 30;
			linkStylePaddingValueLaptop = 35;
		} else if ($hasSeparator) {
			linkStylePaddingValue = 24;
			linkStylePaddingValueLaptop = 24;
		}

		const linkStyle: CSSObject = {
			height: '100%',
			display: 'flex',
			flexDirection: 'row',
			alignItems: 'center',
			gap: 12,
			paddingLeft: linkStylePaddingValue,
			paddingRight: linkStylePaddingValue,
			transition: 'background-color 0.3s ease',
			[`${Navbar} &`]: {
				height: 48,
				[theme.media.desktop.sm]: {
					height: 72,
				},
			},
			'&:hover': {
				backgroundColor: theme.colors.primaryNavHover,
			},
			'body.focusStylesOn &:focus': {
				boxShadow: `inset 0 0 5px 1px ${theme.colors.focusRing}`,
			},
			[theme.media.desktop.laptop]: {
				height: 72,
				paddingLeft: linkStylePaddingValueLaptop,
				paddingRight: linkStylePaddingValueLaptop,
			},
		};
		if ($isActive) style.backgroundColor = theme.colors.primaryNavHover;

		if ($LinkProps?.$paddingLeft !== undefined)
			linkStyle.paddingLeft = $LinkProps.$paddingLeft;
		if ($LinkProps?.$paddingRight !== undefined)
			linkStyle.paddingRight = $LinkProps.$paddingRight;
		if ($LinkProps?.$paddingTop !== undefined)
			linkStyle.paddingTop = $LinkProps.$paddingTop;
		if ($LinkProps?.$paddingBottom !== undefined)
			linkStyle.paddingBottom = $LinkProps.$paddingBottom;
		if ($LinkProps?.$gap !== undefined) linkStyle.gap = $LinkProps.$gap;

		if ($breakpoints !== undefined) {
			Object.entries($breakpoints).forEach(([breakpoint, props]) => {
				const breakpointKey = `@media (min-width: ${breakpoint}px)`;

				const nestedLinkStyle = (
					linkStyle[breakpointKey] ? linkStyle[breakpointKey] : {}
				) as CSSObject;

				if (!linkStyle[breakpointKey])
					linkStyle[breakpointKey] = nestedLinkStyle;

				if (props.$LinkProps?.$paddingLeft !== undefined)
					nestedLinkStyle.paddingLeft = props.$LinkProps.$paddingLeft;
				if (props.$LinkProps?.$paddingRight !== undefined)
					nestedLinkStyle.paddingRight =
						props.$LinkProps.$paddingRight;
				if (props.$LinkProps?.$paddingTop !== undefined)
					nestedLinkStyle.paddingTop = props.$LinkProps.$paddingTop;
				if (props.$LinkProps?.$paddingBottom !== undefined)
					nestedLinkStyle.paddingBottom =
						props.$LinkProps.$paddingBottom;
				if (props.$LinkProps?.$gap !== undefined)
					nestedLinkStyle.gap = props.$LinkProps.$gap;
			});
		}

		style[`${Link}`] = linkStyle;

		return style;
	},
).withComponent('li');

export default MenuItem;
